import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {KeyboardActionsEnum} from '../../../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {NumberInputBox} from '../../../../../../libs/shared/ui/src/components/boxedInputs/numberInputBox';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {useInteractiveListOnCancel} from '../../../../../../libs/shared/ui/src/components/dropdowns/quickInsert/quickInsertCommon';
import {SimpleDropdownWithHeader} from '../../../../../../libs/shared/ui/src/components/dropdowns/simpleDropdown/simpleDropdownWithHeader';
import {KeyboardShortcuts} from '../../../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {Anchor} from '../../../../../../libs/shared/ui/src/components/reposition/repositionHelpers';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';

/*
 * Props.
 */

export type InsertTableDefinition = {rows: number; cols: number};

interface QuickInsertTableDropdownProps {
  anchor: Anchor;
  onSelect: (definition: InsertTableDefinition) => void;
  onCancel: () => void;
}

/*
 * Style.
 */

const StyledDiv = styled.div`
  padding: 5px 10px;
`;

/*
 * Component.
 */

export const QuickInsertTableDropdown: FC<QuickInsertTableDropdownProps> = (props) => {
  const [onRequestClose, onConfirm] = useInteractiveListOnCancel(props.onCancel, props.onSelect);
  const [rows, setRows] = useState<number | ''>(1);
  const [cols, setCols] = useState<number | ''>(2);
  const saveDisabled = !rows || !cols || rows < 1 || cols < 2;
  const onEnter = () => {
    if (saveDisabled) {
      return;
    }
    onConfirm({rows, cols});
  };

  return (
    <SimpleDropdownWithHeader
      width={150}
      title={<Trans id="quickinsert-table-insert-title">Insert a table</Trans>}
      anchor={props.anchor}
      onRequestClose={onRequestClose}
      footer={
        <ButtonGroup>
          <Button onClick={onEnter} variant={ButtonVariantsEnum.PRIMARY} isDisabled={saveDisabled}>
            <Trans id="quickinsert-table-insert">Insert</Trans>
          </Button>
        </ButtonGroup>
      }
    >
      <KeyboardShortcuts
        handlers={{
          [KeyboardActionsEnum.CONFIRM]: onEnter,
        }}
      >
        <StyledDiv>
          <NumberInputBox
            size={VisualSizesEnum.SMALL}
            value={rows}
            onChange={setRows}
            label={<Trans id="quickinsert-table-rows-label">Rows</Trans>}
            shouldAutoFocus
            min={1}
          />
          <NumberInputBox
            size={VisualSizesEnum.SMALL}
            value={cols}
            onChange={setCols}
            label={<Trans id="quickinsert-table-columns-label">Columns</Trans>}
            min={2}
          />
        </StyledDiv>
      </KeyboardShortcuts>
    </SimpleDropdownWithHeader>
  );
};
